/**
 * ftfm-checkout-plus.js — Checkout+ Package Protection auto-add/swap/remove logic.
 * Loaded on cart + PDP templates (see layout/theme.liquid).
 *
 * Pricing tiers are fixed variant IDs on the "Checkout+ Package Protection" product
 * (gid://shopify/Product/9741787726053). Never recompute price client-side.
 *
 * REWRITE (Aug 18): every fee-line removal used to call /cart/change.js with
 * { line: <line KEY> }. The Cart API's `line` is a 1-based integer INDEX; a key must
 * be sent as `id`. So every removal 404'd silently (cartFetch never checked r.ok) and:
 *   - tier swaps kept the old line and /cart/add.js merged each "re-add" into one
 *     growing quantity (the 3 x $5.96 stack);
 *   - the orphan-strip after the last merch item failed, and the reload fallback
 *     re-ran the same failing pass on every load — the visible reload loop.
 * Now: removals go through `id`, responses are checked, one serialized idempotent
 * reconcile decides the whole target state, and nothing here ever reloads the page —
 * the cart section listens for ftfm:checkout-plus-synced and re-renders in place.
 */
(function () {
  'use strict';

  var VARIANTS = {
    standard: 51814368739557,   // $3.09 — tee / boxy tee / lounge shorts
    heavyweight: 51814368772325, // $5.52 — hoodie or crewneck (single garment)
    set: 51814368805093          // $5.96 — any bundle Set
  };
  var VARIANT_IDS = [VARIANTS.standard, VARIANTS.heavyweight, VARIANTS.set];
  var OPTOUT_KEY = 'ftfmCheckoutPlusOptOut';
  var DEBOUNCE_MS = 300;

  // true only while OUR OWN /cart/*.js mutations run, so the fetch hook at the
  // bottom never re-triggers a sync off the sync's own requests.
  var inFlight = false;
  var debounceTimer = null;

  function isOptedOut() {
    try { return sessionStorage.getItem(OPTOUT_KEY) === '1'; } catch (e) { return false; }
  }
  function setOptedOut(v) {
    try { sessionStorage.setItem(OPTOUT_KEY, v ? '1' : '0'); } catch (e) {}
  }

  function determineTier(realItems) {
    var hasSet = realItems.some(function (i) { return (i.product_title || '').indexOf('Set —') !== -1; });
    if (hasSet) return VARIANTS.set;
    var hasHoodieOrCrew = realItems.some(function (i) {
      return /hoodie|crewneck/i.test(i.product_title || '');
    });
    if (hasHoodieOrCrew) return VARIANTS.heavyweight;
    return VARIANTS.standard;
  }

  function cartFetch(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) { throw new Error(url + ' responded ' + r.status); }
      return r.json();
    });
  }

  // Line-item KEY goes in `id`. Never use `line` here — that is an integer index,
  // and passing a key to it is the bug this rewrite removes.
  function setLineQty(key, qty) {
    return cartFetch('/cart/change.js', { id: key, quantity: qty });
  }

  // One idempotent reconcile pass against FRESH /cart.js state.
  // Decides the full target state (no fee, or exactly one fee line, qty 1, right
  // tier) and applies the smallest set of mutations. Resolves true if it changed
  // the cart. Never touches the page — UI refresh is the synced event's job.
  function reconcile() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) { throw new Error('/cart.js responded ' + r.status); }
        return r.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var cpLines = items.filter(function (i) { return VARIANT_IDS.indexOf(i.variant_id) !== -1; });
        var realItems = items.filter(function (i) { return VARIANT_IDS.indexOf(i.variant_id) === -1; });

        // ORPHAN RULE: no merch left, or the shopper opted out this session →
        // the fee must not exist at all. Strip EVERY fee line so an emptied cart
        // is genuinely empty and never a "pay $3.09 for nothing" cart.
        if (realItems.length === 0 || isOptedOut()) {
          if (cpLines.length === 0) { return false; }
          var strip = Promise.resolve();
          cpLines.forEach(function (l) {
            strip = strip.then(function () { return setLineQty(l.key, 0); });
          });
          return strip.then(function () { return true; });
        }

        var target = determineTier(realItems);
        var keep = null;
        var mutated = false;
        var chain = Promise.resolve();

        // Keep at most ONE line of the target tier; zero out every other fee line
        // (wrong tier, or duplicates however they got there).
        cpLines.forEach(function (l) {
          if (l.variant_id === target && !keep) { keep = l; return; }
          mutated = true;
          chain = chain.then(function () { return setLineQty(l.key, 0); });
        });

        return chain.then(function () {
          if (!keep) {
            // Safe to add without merging surprises: fresh state said no line of
            // this variant exists, and the mutex below means no other reconcile
            // can be adding one concurrently.
            return cartFetch('/cart/add.js', { items: [{ id: target, quantity: 1 }] })
              .then(function () { return true; });
          }
          if (keep.quantity !== 1) {
            // Right tier but stacked — force it back to a single unit.
            return setLineQty(keep.key, 1).then(function () { return true; });
          }
          return mutated;
        });
      });
  }

  // MUTEX: reconciles are serialized on a single promise chain. A trigger that
  // arrives while a pass is running queues exactly ONE follow-up pass; further
  // triggers while one is queued are coalesced into it (the queued pass re-reads
  // /cart.js, so it absorbs them all). This is what makes overlapping add-to-cart /
  // quantity events unable to race two "no fee present → add one" passes into a
  // stacked fee line.
  var chain = Promise.resolve();
  var passQueued = false;
  function scheduleSync() {
    if (passQueued) { return chain; }
    passQueued = true;
    chain = chain.then(function () {
      passQueued = false;
      inFlight = true;
      return reconcile()
        .catch(function (err) {
          // Fail silent by design: on any unexpected API response leave the cart
          // as-is. Never retry in a loop, never reload the page.
          try { console.error('ftfm-checkout-plus sync failed', err); } catch (e) {}
          return false;
        })
        .then(function (mutated) {
          inFlight = false;
          try {
            document.dispatchEvent(new CustomEvent('ftfm:checkout-plus-synced', { detail: { mutated: !!mutated } }));
          } catch (e) {}
        });
    });
    return chain;
  }

  function debouncedSchedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(scheduleSync, DEBOUNCE_MS);
  }

  // Opt-out ("Continue without protection" / unticking the box) and opt-in both go
  // through the SAME reconcile — the flag flips, the next pass enforces it.
  function optOut() {
    setOptedOut(true);
    return scheduleSync();
  }
  function optIn() {
    setOptedOut(false);
    return scheduleSync();
  }

  window.ftfmCheckoutPlus = { sync: debouncedSchedule, syncNow: scheduleSync, optOut: optOut, optIn: optIn, isOptedOut: isOptedOut };

  // Watch for cart mutations fired by the rest of the theme (fetch-based add-to-cart,
  // quantity steppers, the cart page's own line changes) and resync afterward.
  // Our own requests run with inFlight true and are ignored, so a sync can never
  // re-trigger itself.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = nativeFetch.apply(this, arguments);
    if (!inFlight && /\/cart\/(add|change|update)\.js/.test(url)) {
      result.then(debouncedSchedule, function () {});
    }
    return result;
  };

  // Sync on every page load too — the PDP's add-to-cart is a plain form POST (a full
  // navigation the fetch hook never sees), so the landing page's load is where that
  // add gets reconciled. Immediate, not debounced: a fresh load has nothing to coalesce.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleSync(); });
  } else {
    scheduleSync();
  }
})();
